/**
 * 客户端同步助手（纯函数；I/O 由 apps/web/lib/sync 薄包装）。
 *
 * - prepareEvent：把一次本地写操作变成带幂等键的事件信封（id 即幂等键）；
 * - applyPullDelta：把服务端增量（权威值 + extras + 冲突）应用到本地实体表——
 *   以后到权威值为准收敛，extras 以 `${key}#v${version}` 后缀键保留双版本；
 * - sortPending：出站队列筛选（未同步、按时间排序）。
 */

import type { SyncEntityPayload } from "./types";
import type { SyncEntityType, SyncEventEnvelope, ConflictRecord } from "./types";
import { entityKey } from "./types";
import type { PullDeltaItem } from "./server";

export interface PrepareEventInput {
  id: string; // 调用方生成 UUID（幂等键）
  userId: string;
  deviceId: string;
  entityType: SyncEntityType;
  entityId: string;
  operation: "upsert" | "delete";
  entityVersion: number;
  payload: SyncEntityPayload;
  clientCreatedAt: string;
}

/** 新建事件信封（纯函数；id 由调用方生成并作为幂等键持久化复用）。 */
export function prepareEvent(input: PrepareEventInput): SyncEventEnvelope {
  return { ...input };
}

export interface LocalApplyResult {
  entities: Map<string, SyncEntityPayload>;
  conflicts: ConflictRecord[];
  applied: number;
}

/**
 * 应用服务端增量（权威值直接收敛；双版本以 #v{version} 后缀键保留；冲突记录收集）。
 * 游标由调用方从响应体读取并保存（meta.lastSyncCursor）。
 */
export function applyPullDelta(
  entities: Map<string, SyncEntityPayload>,
  items: PullDeltaItem[],
): LocalApplyResult {
  const conflicts: ConflictRecord[] = [];
  let applied = 0;
  for (const item of items) {
    const key = entityKey(item.event.entityType, item.event.entityId);
    if (item.authority == null) {
      if (entities.has(key)) {
        entities.delete(key);
        applied++;
      }
      continue;
    }
    const prev = entities.get(key);
    entities.set(key, item.authority);
    if (prev == null || JSON.stringify(prev) !== JSON.stringify(item.authority)) applied++;
    for (const extra of item.extras) {
      const v = (extra as { version?: number }).version ?? 1;
      entities.set(`${key}#v${v}`, extra);
    }
    if (item.conflict) conflicts.push(item.conflict);
  }
  return { entities, conflicts, applied };
}

/** 出站队列筛选：待同步事件按 clientCreatedAt 升序（先发生先上传）。 */
export function sortPending(events: SyncEventEnvelope[]): SyncEventEnvelope[] {
  return [...events].sort((a, b) => (a.clientCreatedAt < b.clientCreatedAt ? -1 : a.clientCreatedAt > b.clientCreatedAt ? 1 : 0));
}